// src/components/account/ListingsTab.tsx
import React, { useState, useEffect, ChangeEvent } from 'react';

interface ListingsTabProps {
  handleEditListing?: (listing: any) => void;
}

const ListingsTab: React.FC<ListingsTabProps> = ({ handleEditListing }) => {
  const [listings, setListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<number | null>(null);
  const [editingListing, setEditingListing] = useState<any | null>(null);

  useEffect(() => {
    const fetchListings = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No JWT token found');
        setLoading(false);
        return;
      }

      try {
        const userRes = await fetch('https://therevulist.com/wp-json/custom/v1/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!userRes.ok) throw new Error('Failed to fetch user profile');
        const userData = await userRes.json();
        const currentUserId = userData.id;
        setUserId(currentUserId);

        const listingsRes = await fetch(
          `https://therevulist.com/wp-json/directorist/v2/listings?per_page=100`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!listingsRes.ok) throw new Error('Failed to fetch listings');
        const allListings = await listingsRes.json();

        const userListings = allListings.filter((l: any) => l.author === currentUserId);
        setListings(userListings);
      } catch (err) {
        console.error(err);
        setListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, []);

  if (loading) return <div>Loading your listings...</div>;
  if (!listings.length)
    return <div className="bg-white rounded-2xl shadow-sm p-8">No listings yet.</div>;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!editingListing) return;
    const { name, value } = e.target;
    setEditingListing({
      ...editingListing,
      fields: {
        ...editingListing.fields,
        [name]: value,
      },
    });
  };

  const saveListing = async () => {
    if (!editingListing) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      // Directorist zahteva da privacy_policy i terms budu boolean u fields
      const safeFields = {
        ...editingListing.fields,
        privacy_policy: true,
        terms_and_condition: true,
      };

      const body = {
        fields: safeFields,
        status: editingListing.status || "publish",
      };

      const res = await fetch(
        `https://therevulist.com/wp-json/directorist/v2/listings/${editingListing.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Failed to save listing: ${errText}`);
      }

      const updated = await res.json();
      setListings((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      setEditingListing(null);
      alert('Listing updated successfully!');
    } catch (err) {
      console.error('Error updating listing:', err);
      alert('Error updating listing');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">My Listings</h2>
          <a
            href="/pricing"
            className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            Add New Listing
          </a>
        </div>

        <div className="space-y-4">
          {listings.map((listing) => (
            <div
              key={listing.id}
              className="border rounded-lg p-4 shadow hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {listing.fields.title || 'Untitled'}
                  </h3>
                  {listing.fields.address && <p className="text-gray-600">{listing.fields.address}</p>}
                  {listing.fields.phone && <p className="text-gray-600">Phone: {listing.fields.phone}</p>}
                  {listing.fields.email && <p className="text-gray-600">Email: {listing.fields.email}</p>}
                  {listing.fields.website && (
                    <a
                      href={listing.fields.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Website
                    </a>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingListing(listing)}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                  <a
                    href={listing.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                  >
                    View
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editingListing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h2 className="text-xl font-bold mb-4">Edit Listing</h2>

            {['title', 'address', 'phone', 'email', 'website'].map((field) => (
              <div key={field} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </label>
                <input
                  name={field}
                  value={editingListing.fields[field] || ''}
                  onChange={handleInputChange}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            ))}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEditingListing(null)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={saveListing}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingsTab;
